/**
 * Form primitives.
 *
 * Every one of them takes an `error` and renders it NEXT TO THE FIELD. The
 * API answers a failed write with RFC 9457 field errors keyed by path, and
 * throwing that away to show one banner at the top is how an operator ends up
 * re-reading their whole configuration to find the one line that is wrong
 * (L66 — name the fix, not the fault).
 */
import type { ReactNode } from 'react';

export function Field(
  { label, hint, error, children }:
  { label: string; hint?: string | undefined; error?: string | undefined; children: ReactNode }
): ReactNode {
  return (
    <label className="block">
      <span className="block text-sm text-ink-200">{label}</span>
      {hint ? <span className="mt-0.5 block text-xs text-ink-400">{hint}</span> : null}
      <span className="mt-1.5 block">{children}</span>
      {error ? (
        <span role="alert" className="mt-1 block text-xs text-bad-400">{error}</span>
      ) : null}
    </label>
  );
}

const box =
  'w-full rounded-md border border-ink-800 bg-ink-900 px-3 py-2 text-sm text-ink-50 ' +
  'placeholder:text-ink-700 disabled:opacity-40';

export function Text(
  { value, onChange, placeholder, invalid, maxLength }:
  {
    value: string; onChange: (v: string) => void;
    placeholder?: string; invalid?: boolean; maxLength?: number;
  }
): ReactNode {
  return (
    <input
      value={value}
      onChange={(e) => { onChange(e.target.value); }}
      placeholder={placeholder}
      maxLength={maxLength}
      aria-invalid={invalid ?? false}
      className={`${box} ${invalid ? 'border-bad-400/60' : ''}`}
    />
  );
}

export function Num(
  { value, onChange, min, max, disabled }:
  {
    value: number; onChange: (v: number) => void;
    min?: number; max?: number; disabled?: boolean;
  }
): ReactNode {
  return (
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      disabled={disabled ?? false}
      // `step={1}` alone does not stop a paste. The schema refuses a
      // fractional value anyway — theirs was only truthiness-checked, so 2.5
      // silently floored at draw time.
      step={1}
      onChange={(e) => { onChange(Number(e.target.value)); }}
      className={box}
    />
  );
}

export function Choice<T extends string>(
  { value, onChange, options, disabled }:
  {
    value: T; onChange: (v: T) => void;
    options: readonly { value: T; label: string }[]; disabled?: boolean;
  }
): ReactNode {
  return (
    <select
      value={value}
      disabled={disabled ?? false}
      onChange={(e) => { onChange(e.target.value as T); }}
      className={box}
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

export function Check(
  { checked, onChange, label }:
  { checked: boolean; onChange: (v: boolean) => void; label: string }
): ReactNode {
  return (
    <label className="flex items-center gap-2 text-sm text-ink-200">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => { onChange(e.target.checked); }}
        className="size-4 rounded border-ink-700 bg-ink-900"
      />
      {label}
    </label>
  );
}

/**
 * A list of words.
 *
 * Entered one per line rather than comma-separated: a tag containing a comma
 * is a tag that silently becomes two, and a newline is the one separator
 * nobody types by accident.
 */
export function Lines(
  { value, onChange, placeholder }:
  { value: string[]; onChange: (v: string[]) => void; placeholder?: string }
): ReactNode {
  return (
    <textarea
      rows={3}
      value={value.join('\n')}
      placeholder={placeholder}
      onChange={(e) => {
        onChange(e.target.value.split('\n').map((s) => s.trim()).filter((s) => s.length > 0));
      }}
      className={`${box} font-mono`}
    />
  );
}

/**
 * Supply, as three named options rather than a number that means three things.
 *
 * L8 — `null` (uncapped) and `0` (deliberately not printed) are OPPOSITES,
 * and the difference is an infinite print run versus an item that was never
 * meant to exist. A single number input with a "leave blank for unlimited"
 * hint puts them one keystroke apart.
 */
export type SupplyChoice = 'unlimited' | 'limited' | 'none';

export function Supply(
  { choice, limit, onChange, error }:
  {
    choice: SupplyChoice; limit: number;
    onChange: (choice: SupplyChoice, limit: number) => void;
    error?: string | undefined;
  }
): ReactNode {
  return (
    <Field label="Print run" error={error}>
      <div className="space-y-2">
        <Choice
          value={choice}
          onChange={(c) => { onChange(c, limit); }}
          options={[
            { value: 'limited', label: 'Limited — a numbered run' },
            { value: 'unlimited', label: 'Unlimited — unnumbered, never runs out' },
            { value: 'none', label: 'Not printed — cannot be minted at all' },
          ]}
        />
        {choice === 'limited' ? (
          <Num value={limit} onChange={(n) => { onChange(choice, n); }} min={1} max={10_000} />
        ) : null}
        <p className="text-xs text-ink-400">
          {choice === 'limited'
            ? 'Each copy is stamped with its number. This cannot change once a copy exists.'
            : choice === 'unlimited'
              ? 'Copies carry no edition number.'
              : 'Nothing will ever be minted from this. Not the same as unlimited.'}
        </p>
      </div>
    </Field>
  );
}

export function supplyValue(choice: SupplyChoice, limit: number): { limit: number | null } {
  // The one place the three choices become the wire value, so the null/zero
  // distinction is made once rather than at every call site.
  switch (choice) {
    case 'unlimited': return { limit: null };
    case 'none': return { limit: 0 };
    case 'limited': return { limit };
  }
}
